import { BusinessObjectBase } from './base';
import { serviceTaskSchema } from '@schemas/business-objects/service-task';
import type { ServiceTaskHistory } from './service-task-history';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { Id, Json, ServiceTaskStatus } from '../base-types';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceHistory } from './process-instance-history';

export interface ServiceTaskData {
  readonly id: Id;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly queue?: string;
  readonly status?: ServiceTaskStatus;
  readonly attempts?: number;
  readonly claimedBy?: string;
  readonly claimedAt?: Date;
  readonly errorMessage?: string;
  readonly errorCode?: string;
  readonly data?: Json;
  readonly completedAt?: Date;
  readonly loopIndex?: number;
  readonly resultApplied?: boolean;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly serviceTaskHistories?: ServiceTaskHistory[];
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ServiceTask extends Readonly<ServiceTaskData> {}
type ServiceTaskCreateWithChildrenData = Partial<
  Omit<ServiceTaskData, 'serviceTaskHistories'>
> & { serviceTaskHistories?: Partial<ServiceTaskHistory>[] };

export class ServiceTask extends BusinessObjectBase {
  static readonly entityName = 'ServiceTask' as const;
  static readonly plural = 'service-tasks';
  static schema = serviceTaskSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ServiceTask[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ServiceTask(data));
  }

  static async findOne(filter?: any): Promise<ServiceTask | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ServiceTask(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ServiceTask(data);
  }

  static async create(data: Omit<ServiceTaskData, 'id'>): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ServiceTask(result);
  }

  static async createMany(
    data: Omit<ServiceTaskData, 'id'>[],
  ): Promise<ServiceTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ServiceTask(item));
  }

  static async createWithChildren(
    data: ServiceTaskCreateWithChildrenData,
  ): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ServiceTask(result);
  }

  static async createManyWithChildren(
    data: ServiceTaskCreateWithChildrenData[],
  ): Promise<ServiceTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ServiceTask(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ServiceTaskData>,
  ): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ServiceTask(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async claimNext(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/claim-next`;
    return this.post<any>(url, options);
  }
  async claim(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/claim`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async complete(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/complete`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async error(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/error`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async fail(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/fail`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
